import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { PageHeaderComponent } from '../../../components/shared/page-header/page-header.component';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { TagModule } from 'primeng/tag';
import { MenuModule } from 'primeng/menu';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { DrawerModule } from 'primeng/drawer';
import { TabsModule } from 'primeng/tabs';
import { Router } from '@angular/router';
import { UserService } from './user.service';
import { getUserDataTableStructure } from './utils/user-data-table-object';
import { DataTableRowActions, DynamicDataTable } from '../../../components/data-table-builder/data-table.types';
import { DataTableBuilderComponent } from '../../../components/data-table-builder/data-table-builder.component';
import { DialogObject } from '../../../components/dialog/dialog.types';
import { DialogComponent } from '../../../components/dialog/dialog.component';
import { MessageService } from 'primeng/api';
import { getUserDeleteObject } from './utils/user-delete-dialog-object';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UserRepository } from '@/store/user-state/user-state.repository';

@Component({
  selector: 'sm-user',
  imports: [
    PageHeaderComponent,
    CommonModule,
    ButtonModule,
    TableModule,
    IconFieldModule,
    InputIconModule,
    TagModule,
    MenuModule,
    InputTextModule,
    FormsModule,
    SelectModule,
    DrawerModule,
    TabsModule,
    DataTableBuilderComponent,
    DialogComponent,
    TranslateModule,
  ],
  templateUrl: './user.component.html',
  styleUrl: './user.component.scss',
})
export class UserComponent implements OnInit {
  userService = inject(UserService);
  userRepository = inject(UserRepository);
  messageService = inject(MessageService);
  translate = inject(TranslateService);
  router = inject(Router);

  //breadcrumb
  breadcrumb = [
    {
      label: 'settings.user.breadcrumb.parametres',
      routerLink: ['/settings'],
    },
    {
      label: 'settings.user.breadcrumb.user',
      routerLink: ['/settings', 'user'],
    },
  ];

  @ViewChild('dialog') dialogComponent!: DialogComponent;
  userDialog?: DialogObject;

  rowActions: DataTableRowActions = {
    inspectAction: row => {
      this.userRepository.set('response', row);
      this.router.navigate(['/settings', 'user', 'update', row.id], { queryParams: { inspect: true } });
    },
    editAction: row => {
      this.userRepository.set('response', row);
      this.router.navigate(['/settings', 'user', 'update', row.id]);
    },
    deleteAction: row => {
      this.userRepository.set('response', row);
      this.userDialog = getUserDeleteObject({
        t: this.translate,
        store: this.userRepository,
        representation: `${row.firstName} ${row.lastName}`,
        action: () => {
          this.deleteUser(row.id);
          this.dialogComponent.toggleVisible();
        },
        toggleVisiblity: () => this.dialogComponent.toggleVisible(),
      });
      this.dialogComponent.toggleVisible();
    },
  };

  data: any[] = [];
  dataTableObject: DynamicDataTable = getUserDataTableStructure({
    store: this.userRepository,
    createAction: () => this.router.navigate(['/settings', 'user', 'add']),
    rowActions: this.rowActions,
  });
  loading = true;

  ngOnInit(): void {
    (this.userRepository as any).enableUserSwitchHandler = this.onEnableUserSwitch.bind(this);
    this.loadData();
  }

  loadData() {
    this.userService.getUsers().subscribe({
      next: data => {
        this.data = data;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: 'Une erreur est survenue lors de la récupération des utilisateurs',
        });
      },
    });
  }

  deleteUser(id: string) {
    this.userService.deleteUser(id).subscribe({
      next: () => {
        this.loadData();
        this.userService.reset();
        this.loading = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: "L'utilisateur a été supprimé avec succès",
        });
      },
      error: error => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: error.error?.message || "Une erreur est survenue lors de la suppression de l'utilisateur",
        });
      },
    });
  }

  onEnableUserSwitch(checked: any, row: any) {
    const action = checked.checked
      ? this.translate.instant('settings.user.enable.actionEnable')
      : this.translate.instant('settings.user.enable.actionDisable');
    this.userDialog = {
      title: this.translate.instant('settings.user.enable.confirmTitle'),
      description: this.translate.instant('settings.user.enable.confirmDescription', {
        action,
        user: row.username,
      }),
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onHide: () => {},
      actions: [
        {
          label: this.translate.instant('common.confirm'),
          variant: 'success',
          onClick: () => {
            this.dialogComponent.toggleVisible();
            this.setUserEnabled(row.id, checked.checked);
          },
        },
        {
          label: this.translate.instant('common.cancel'),
          variant: 'secondary',
          onClick: () => {
            this.dialogComponent.toggleVisible();
          },
        },
      ],
    };
    this.dialogComponent.toggleVisible();
  }

  setUserEnabled(userId: string, enabled: boolean) {
    this.userService.enableUser(userId, enabled).subscribe({
      next: () => {
        this.loadData();
        this.messageService.add({
          severity: 'success',
          summary: enabled ? 'Utilisateur activé' : 'Utilisateur désactivé',
          detail: enabled ? "L'utilisateur a été activé avec succès." : "L'utilisateur a été désactivé avec succès.",
        });
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: "Une erreur est survenue lors de la modification de l'état de l'utilisateur.",
        });
      },
    });
  }
}
